import React, { useState, useRef, useEffect } from 'react';
import { Slide, SlideImage } from '../types';
import { slideApiService } from '../services/slideApiService';
import { useShop } from '../contexts/ShopContext';
import AuthenticatedImage from './AuthenticatedImage';
import SlideViewModal from './SlideViewModal';
import AddToCollectionButton from './AddToCollectionButton';
import SlideCollectionsPickerModal from './SlideCollectionsPickerModal';
import FavoriteButton from './FavoriteButton';

interface CompactSlideCardProps {
  slide: Slide;
  onSlideUpdated?: (slide: Slide) => void;
  onSlideDeleted?: (slideId: string) => void;
  onSlideEdited?: (slide: Slide) => void;
  isAnalyzing?: boolean;
  enableDrag?: boolean;
}

const CompactSlideCard: React.FC<CompactSlideCardProps> = ({
  slide,
  onSlideUpdated,
  onSlideDeleted,
  onSlideEdited,
  isAnalyzing = false,
  enableDrag = false
}) => {
  const { isInternalMember } = useShop();
  const [showViewModal, setShowViewModal] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageHovered, setImageHovered] = useState(false);
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [prefetchTriggered, setPrefetchTriggered] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const [heicPolling, setHeicPolling] = useState(false);

  // HEIC polling: periodically re-fetch slide data to pick up new image ID after conversion
  useEffect(() => {
    if (!heicPolling) return;
    const interval = setInterval(async () => {
      try {
        const updated = await slideApiService.getSlideById(slide.shopId, slide.id);
        const oldIds = (slide.images || []).map(img => img.id).sort().join(',');
        const newIds = (updated.images || []).map((img: SlideImage) => img.id).sort().join(',');
        if (oldIds !== newIds) {
          setHeicPolling(false);
          onSlideUpdated?.({ ...slide, images: updated.images || [] });
        }
      } catch { }
    }, 3000);
    const timeout = setTimeout(() => setHeicPolling(false), 120000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [heicPolling, slide, onSlideUpdated]);

  const swipedRef = useRef(false);
  const touchStartRef = useRef<number | null>(null);
  const touchEndRef = useRef<number | null>(null);
  const dragStartRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const minSwipeDistance = 50;

  const sortedImages = slide.images
    ? [...slide.images].sort((a, b) => a.displayOrder - b.displayOrder)
    : [];
  const hasMultipleImages = sortedImages.length > 1;

  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

  // IntersectionObserver for mobile viewport-based prefetch and arrow visibility
  useEffect(() => {
    if (!isTouchDevice || !imageContainerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(entry.isIntersecting);
        if (entry.isIntersecting) setPrefetchTriggered(true);
      },
      { threshold: 0.5 }
    );
    observer.observe(imageContainerRef.current);
    return () => observer.disconnect();
  }, [isTouchDevice]);

  const triggerPrefetch = () => {
    if (!prefetchTriggered && hasMultipleImages) setPrefetchTriggered(true);
  };

  const showArrows = hasMultipleImages && imageHovered && !isTouchDevice;

  // Arrow click handlers
  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerPrefetch();
    setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerPrefetch();
    setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
  };

  // Touch handlers (mobile swipe)
  const onTouchStart = (e: React.TouchEvent) => {
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (touchStartRef.current === null || touchEndRef.current === null || !hasMultipleImages) {
      touchStartRef.current = null;
      touchEndRef.current = null;
      return;
    }
    const distance = touchStartRef.current - touchEndRef.current;
    if (distance > minSwipeDistance) {
      triggerPrefetch();
      setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
      swipedRef.current = true;
    } else if (distance < -minSwipeDistance) {
      triggerPrefetch();
      setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
      swipedRef.current = true;
    }
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  // Mouse drag handlers (desktop)
  const onDragStart = (e: React.MouseEvent) => {
    dragStartRef.current = e.clientX;
    isDraggingRef.current = false;
  };

  const onDragMove = (e: React.MouseEvent) => {
    if (dragStartRef.current !== null && Math.abs(e.clientX - dragStartRef.current) > 5) {
      isDraggingRef.current = true;
    }
  };

  const onDragEnd = (e: React.MouseEvent) => {
    if (dragStartRef.current !== null && isDraggingRef.current && hasMultipleImages) {
      const distance = dragStartRef.current - e.clientX;
      if (distance > minSwipeDistance) {
        triggerPrefetch();
        setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
        swipedRef.current = true;
      } else if (distance < -minSwipeDistance) {
        triggerPrefetch();
        setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
        swipedRef.current = true;
      }
    }
    dragStartRef.current = null;
    isDraggingRef.current = false;
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (!enableDrag) return;
    setIsDragActive(true);
    const dragData = {
      slideId: slide.id,
      currentLocationId: slide.locationId,
      currentPlaceId: slide.placeId
    };
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragActive(false);
  };

  return (
    <>
      <div
        draggable={enableDrag}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={{ opacity: isDragActive ? 0.5 : 1, cursor: enableDrag ? 'grab' : undefined }}
      >
      <div
        ref={imageContainerRef}
        onClick={() => {
          if (swipedRef.current) {
            swipedRef.current = false;
            return;
          }
          setShowViewModal(true);
        }}
        onMouseEnter={() => {
          setImageHovered(true);
          triggerPrefetch();
        }}
        onMouseLeave={() => {
          setImageHovered(false);
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onDragStart}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        style={{
          position: 'relative',
          cursor: 'pointer',
          overflow: 'hidden',
          borderRadius: '2px',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      >
        {/* Images */}
        {sortedImages.length > 0 ? (
          sortedImages.map((img, index) => {
            if (index !== currentImageIndex && !prefetchTriggered) return null;
            return (
              <AuthenticatedImage
                key={img.id}
                shopId={slide.shopId}
                slideId={slide.id}
                imageId={img.id}
                alt={slide.name}
                loading={index === currentImageIndex ? 'eager' : (prefetchTriggered ? 'eager' : 'lazy')}
                size="thumb_300"
                onHeicPending={() => setHeicPolling(true)}
                style={{
                  width: '100%',
                  aspectRatio: '1/1',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  ...(index !== currentImageIndex ? { position: 'absolute', visibility: 'hidden' } : {})
                }}
              />
            );
          })
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: '1/1',
              backgroundColor: 'var(--bg-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-secondary)'
            }}
          >
            <div style={{ fontSize: '1.5rem' }}>&#128444;</div>
          </div>
        )}

        {/* Sold Indicator — black "$" on a white chip (top-left). active/sold are shop-internal
            state; never shown to external (CLIENT/VENDOR) members (projection omits these fields). */}
        {isInternalMember && slide.sold && (
          <div
            style={{
              position: 'absolute',
              top: '6px',
              left: '6px',
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: '11.5px',
              lineHeight: 1,
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)'
            }}
            title="Sold"
          >$</div>
        )}

        {/* Inactive — golden frame around the whole card (replaces the red corner dot). Internal-only. */}
        {isInternalMember && !slide.active && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              border: '3px solid var(--warning)',
              borderRadius: '2px',
              boxSizing: 'border-box',
              pointerEvents: 'none',
              zIndex: 2
            }}
          />
        )}

        {/* Published Indicator (bottom-left) — internal-only, mirrors the sold (top-left) and
            inactive (top-right) status dots. */}
        {isInternalMember && slide.published && (
          <div
            style={{
              position: 'absolute',
              bottom: '6px',
              left: '6px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: 'var(--primary-light)',
              border: '2px solid rgba(255, 255, 255, 0.8)',
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
            }}
            title="Published"
          />
        )}

        {/* Left arrow */}
        {hasMultipleImages && (
          <button
            onClick={handlePrevImage}
            style={{
              position: 'absolute',
              left: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'var(--text-primary)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
              opacity: showArrows ? 0.9 : 0,
              transition: 'opacity 0.2s ease',
              zIndex: 1,
              padding: 0,
              lineHeight: 1,
              WebkitTapHighlightColor: 'transparent'
            }}
            aria-label="Previous image"
          >
            &#8249;
          </button>
        )}

        {/* Right arrow */}
        {hasMultipleImages && (
          <button
            onClick={handleNextImage}
            style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'var(--text-primary)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
              opacity: showArrows ? 0.9 : 0,
              transition: 'opacity 0.2s ease',
              zIndex: 1,
              padding: 0,
              lineHeight: 1,
              WebkitTapHighlightColor: 'transparent'
            }}
            aria-label="Next image"
          >
            &#8250;
          </button>
        )}

        {/* Dot indicators */}
        {hasMultipleImages && (
          <div
            style={{
              position: 'absolute',
              bottom: '4px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }}
          >
            {sortedImages.slice(0, 7).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: index === currentImageIndex ? 'var(--bg-card)'
                    : (sortedImages.length > 7 && currentImageIndex >= 7 && index === 6) ? 'white'
                    : 'rgba(255, 255, 255, 0.5)',
                  transition: 'background-color 0.2s ease'
                }}
              />
            ))}
          </div>
        )}

        {/* Analyzing indicator (bottom-left) */}
        {isAnalyzing && (
          <div
            style={{
              position: 'absolute',
              bottom: '6px',
              left: '6px',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: 'var(--primary)',
              animation: 'pulse 1.5s ease-in-out infinite',
              boxShadow: '0 0 4px rgba(74, 107, 138, 0.6)'
            }}
          />
        )}

        <AddToCollectionButton visible={imageHovered} size={20} right={36} onClick={() => setShowCollectionPicker(true)} />
        <FavoriteButton slideId={slide.id} size={20} />
      </div>

      {/* Quick add to collections; rendered outside the card so it can't trigger the card's handlers. */}
      {showCollectionPicker && (
        <SlideCollectionsPickerModal slide={slide} onClose={() => setShowCollectionPicker(false)} />
      )}

      {/* View Modal */}
      {showViewModal && (
        <SlideViewModal
          slide={slide}
          onClose={() => setShowViewModal(false)}
          onEdit={(slide) => {
            setShowViewModal(false);
            onSlideEdited?.(slide);
          }}
        />
      )}
      </div>
    </>
  );
};

export default CompactSlideCard;
